// ---------------------------------------------------------------------------
// The app's brand mark: a white tooth with a teal sparkle on a navy→teal
// gradient tile. The same artwork ships as public/favicon.svg — keep the two
// in sync so the tab icon, sign-in page, top bar, and onboarding all match.
// ---------------------------------------------------------------------------

import { Box, Stack, Typography, type SxProps, type Theme } from '@mui/material';

/** Tooth silhouette (crown + two roots), drawn in a 24×24 box. */
const TOOTH_PATH =
  'M12 3.2c-2.3 0-3.1 1.1-4.4 1.1-1.9 0-3.4 1.6-3.4 4 0 1.9.7 3.1 1.4 4.6.6 1.3.9 3.1 1 5.5.1 1.3.5 3.2 1.8 3.2 1.8 0 1.1-4.1 3.6-4.1s1.8 4.1 3.6 4.1c1.3 0 1.7-1.9 1.8-3.2.1-2.4.4-4.2 1-5.5.7-1.5 1.4-2.7 1.4-4.6 0-2.4-1.5-4-3.4-4-1.3 0-2.1-1.1-4.4-1.1z';

/** Four-point sparkle used as the "coach polish" accent. */
const SPARKLE_PATH = 'M12 0l1.2 4.8L18 6l-4.8 1.2L12 12l-1.2-4.8L6 6l4.8-1.2z';

export function BrandMark({ size = 32 }: { size?: number }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 48 48"
      aria-hidden
      sx={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    >
      <defs>
        <linearGradient id="dic-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1f3a5f" />
          <stop offset="100%" stopColor="#2a9d8f" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="46" height="46" rx="11" fill="url(#dic-bg)" />
      <g transform="translate(6.5, 7) scale(1.42)">
        <path d={TOOTH_PATH} fill="#ffffff" />
      </g>
      <g transform="translate(30, 8) scale(0.8)">
        <path d={SPARKLE_PATH} fill="#9be8dd" />
      </g>
    </Box>
  );
}

/** Logo + wordmark lockup for headers and the sign-in page. */
export default function BrandLogo({
  size = 32,
  withName = true,
  nameSx,
}: {
  size?: number;
  withName?: boolean;
  nameSx?: SxProps<Theme>;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <BrandMark size={size} />
      {withName && (
        <Typography
          variant="h6"
          noWrap
          sx={{ fontWeight: 700, letterSpacing: '-0.01em', minWidth: 0, ...nameSx }}
        >
          Dental Interview Coach
        </Typography>
      )}
    </Stack>
  );
}
